/**
 * Common validation utilities that can be shared across forms
 */
import {
  CAREER_EMAIL_REGEX,
  ERROR_MESSAGES,
  MAX_EMAIL_LENGTH,
  FILE_SIZE_LIMIT_MB,
} from '../../constants';

/* ------------------------------
 * Atomic Validators (UNCHANGED)
 * -----------------------------*/

 
export const validateEmail = (email) => {
  if (!email || !email.trim()) {
    return ERROR_MESSAGES.EMAIL_REQUIRED;
  }
 
  const trimmedEmail = email.trim();
 
  if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
    return ERROR_MESSAGES.EMAIL_TOO_LONG;
  }
 
  const emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
 
  if (!emailRegex.test(trimmedEmail)) {
    return ERROR_MESSAGES.EMAIL_INVALID;
  }
 
  return null;
};
 
/**
 * Career form email validation using strict regex (no consecutive/leading/trailing dots in local part).
 * ReDoS-safe: no nested quantifiers or unbounded lookaheads.
 */
export const validateCareerEmail = (email) => {
  if (!email || !email.trim()) {
    return ERROR_MESSAGES.EMAIL_REQUIRED;
  }
 
  const trimmedEmail = email.trim();
 
  if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
    return ERROR_MESSAGES.EMAIL_TOO_LONG;
  }
 
  if (!CAREER_EMAIL_REGEX.test(trimmedEmail)) {
    return ERROR_MESSAGES.EMAIL_INVALID;
  }
 
  return null;
};
 
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || !String(value).trim()) {
    return ERROR_MESSAGES.FIELD_REQUIRED(fieldName);
  }
  return null;
};
 
export const validateFileSize = (
  file,
  maxSizeInMB = FILE_SIZE_LIMIT_MB,
  allowedTypes = ["image/jpeg", "image/png", "application/pdf"]
) => {
  if (!file) {
    return ERROR_MESSAGES.FILE_REQUIRED;
  }
 
  if (!allowedTypes.includes(file.type)) {
    return ERROR_MESSAGES.INVALID_FILE_TYPE;
  }
 
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    return ERROR_MESSAGES.FILE_SIZE_EXCEEDED(maxSizeInMB);
  }
 
  return null;
};
 
export const validateMobileNumber = (mobileNumber) => {
  if (!mobileNumber || !mobileNumber.trim()) {
    return ERROR_MESSAGES.MOBILE_REQUIRED;
  }
 
  const mobileRegex = /^\d{10}$/;
  const normalized = mobileNumber
    .trim()
    .replace(/[\s-()]/g, '');
 
  if (!mobileRegex.test(normalized)) {
    return ERROR_MESSAGES.MOBILE_INVALID;
  }
 
  return null;
};
 
export const validatePolicyorClaimNumber = (value, fieldName = "Field") => {
  if (!value) {
    return ERROR_MESSAGES.VALUE_REQUIRED(fieldName);
  }

  const cleanValue = value.trim();

  if (fieldName === "policy number") {
    if (!/^\d+$/.test(cleanValue)) {
      return ERROR_MESSAGES.VALUE_INVALID(fieldName);
    }
    if (cleanValue.length !== 8) {
      return ERROR_MESSAGES.VALUE_INVALID(fieldName);
    }
  }

  if (fieldName === "claim reference number") {
    if (!/^[a-zA-Z0-9]+$/.test(cleanValue)) {
      return ERROR_MESSAGES.VALUE_INVALID(fieldName);
    }

    if (cleanValue.length < 13 || cleanValue.length > 14) {
      return ERROR_MESSAGES.VALUE_INVALID(fieldName);
    }
  }

  return null;
};
 
export const validateBankDetails = (value, fieldName) => {
    if (!value) {
        return ERROR_MESSAGES.VALUE_REQUIRED(fieldName);
    }
 
    const trimmedValue = typeof value === "string" ? value.trim() : value;
 
    switch (fieldName) {
 
        case "Account Number":
            if (!/^\d{9,18}$/.test(trimmedValue)) {
                return ERROR_MESSAGES.VALUE_INVALID(fieldName);
            }
            break;
 
        case "Bank Name":
            if (!/^[A-Za-z\s]{3,}$/.test(trimmedValue)) {
                return ERROR_MESSAGES.VALUE_INVALID(fieldName);
            }
            break;
 
        case "IFSC Code": {
            const formattedIFSC = trimmedValue.toUpperCase();
            if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formattedIFSC)) {
                return ERROR_MESSAGES.VALUE_INVALID(fieldName);
            }
            break;
        }
 
        case "MICR Code":
            if (!/^\d{9}$/.test(trimmedValue)) {
                return ERROR_MESSAGES.VALUE_INVALID(fieldName);
            }
            break;
 
        case "Branch Name":
            if (!/^[A-Za-z\s]{3,}$/.test(trimmedValue)) {
                return ERROR_MESSAGES.VALUE_INVALID(fieldName);
            }
            break;
 
        default:
            return true;
    }
 
    return null;
};
 
export const validatePincode = (pincode) => {
  if (!pincode || !pincode.trim()) {
    return ERROR_MESSAGES.FIELD_REQUIRED('PIN Code');
  }
 
  const pincodeRegex = /^\d{6}$/;
  if (!pincodeRegex.test(pincode.trim())) {
    return 'Enter valid 6-digit PIN';
  }
 
  return null;
};

/* ------------------------------------------------
 * ✅ SCHEMA-BASED FORM VALIDATOR (NEW)
 * ------------------------------------------------*/
// careerApplicationSchema json
export const careerApplicationSchema = {
  name: {
    required: true,
    label: 'Name',
  },
  mobileNumber: {
    required: true,
    mobile: true,
  },
  email: {
    required: true,
    careerEmail: true,
  },
  department: {
    required: true,
    label: 'Department',
  },
   departmentRole: {
    required: true,
    label: 'Role',
  },
  resume: {
    required: true,
    file: true,
    maxSizeMB: 5,
  },
};

// becomeAdvisorSchema json

export const becomeAdvisorSchema = {
  name: {
    required: true,
    label: 'Name',
  },
  mobile: {
    required: true,
    mobile: true,
  },
  email: {
    required: true,
    email: true,
  },
  pincode: {
    required: true,
    pincode: true,
  },
  state: {
    required: true,
    label: 'State',
  },
  city: {
    required: true,
    label: 'City',
  },
};

// selfFormSchema.js (or inside validation.js)

export const selfFormSchema = {
  name: {
    required: true,
    label: "Full Name",
  },

  mobile: {
    required: true,
    mobile: true,
  },

  email: {
    required: true,
    email: true,
  },

  groupCompanyName: {
    required: true,
    label: "Group Company Name",
  },

  employeeCode: {
    required: true,
    label: "Employee Code",
  },

  policyNumber: {
    required: true,
    label: "Policy Number",
  },

  category: {
    required: true,
    label: "Category",
  },

  concerns: {
    required: true,
    label: "Concerns",
    maxLength: 150,
  },
};

export const advisorFormSchema = {
  // Policy Holder
  name: {
    required: true,
    label: "Full Name",
  },

  mobile: {
    required: true,
    mobile: true,
  },

  email: {
    required: true,
    email: true,
  },

  employeeCode: {
    required: true,
    label: "Employee Code",
  },

  category: {
    required: true,
    label: "Category",
  },

  // Advisor
  advisorName: {
    required: true,
    label: "Advisor Name",
  },

  advisorMobileNumber: {
    required: true,
    mobile: true,
  },

  advisorEmailID: {
    required: true,
    email: true,
  },

  advisorCode: {
    required: true,
    label: "Advisor Code",
  },

  // Common
  concerns: {
    required: true,
    label: "Concerns",
    maxLength: 150,
  },
};

export const otherCustomerFormSchema = {
  /* ======================
     Policy Holder Details
     ====================== */

  name: {
    required: true,
    label: "Full Name",
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Za-z\s]+$/,
    patternMessage: "Only alphabets are allowed",
  },

  mobile: {
    required: true,
    label: "Mobile Number",
    mobile: true,
  },

  email: {
    required: true,
    label: "Email ID",
    email: true,
  },

  employeeCode: {
    required: true,
    label: "Employee Code",
    minLength: 1,
    maxLength: 10,
  },

  category: {
    required: true,
    label: "Category",
  },

  /* ======================
     Other Customer Details
     ====================== */

  customerName: {
    required: true,
    label: "Customer Full Name",
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Za-z\s]+$/,
    patternMessage: "Only alphabets are allowed",
  },

  customerMobileNumber: {
    required: true,
    label: "Customer Mobile Number",
    mobile: true,
  },

  customerEmailID: {
    required: true,
    label: "Customer Email ID",
    email: true,
  },

  /* ======================
     Concerns
     ====================== */

  concerns: {
    required: true,
    label: "Concerns",
    minLength: 5,
    maxLength: 150,
  },
};






/**
 * Validates a single field value against its rules
 * @param {*} value - field value
 * @param {object} rules - validation rules for the field
 * @param {string} fieldName - name of the field (for error messages)
 * @returns {string|null} error message or null if valid
 */
const validateFieldValue = (value, rules, fieldName) => {
  const validators = [
    { condition: rules.required, validator: () => validateRequired(value, rules.label || fieldName) },
    { condition: rules.email, validator: () => validateEmail(value) },
    { condition: rules.careerEmail, validator: () => validateCareerEmail(value) },
    { condition: rules.mobile, validator: () => validateMobileNumber(value) },
    { condition: rules.customerMobileNumber, validator: () => validateMobileNumber(value) },
    { condition: rules.advisorMobileNumber, validator: () => validateMobileNumber(value) },
    { condition: rules.pincode, validator: () => validatePincode(value) },
    { condition: rules.file, validator: () => validateFileSize(value, rules.maxSizeMB) },
    {
      condition: rules.maxLength,
      validator: () =>
        value && value.length > rules.maxLength
          ? `Maximum ${rules.maxLength} characters allowed`
          : null,
    },
  ];

  for (const { condition, validator } of validators) {
    if (condition) {
      const err = validator();
      if (err) return err;
    }
  }

  return null;
};

/**
 * Generic schema-based validation runner
 * @param {object} data - form data
 * @param {object} schema - validation schema
 * @returns {object} errors
 */
export const validateBySchema = (data, schema) => {
  const errors = {};

  Object.keys(schema).forEach((field) => {
    const rules = schema[field];
    const value = data[field];
    const error = validateFieldValue(value, rules, field);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

export const validateFieldBySchema = (name, value, schema) => {
  if (!schema[name]) return null;

  const rules = schema[name];
  return validateFieldValue(value, rules, name);
};



/**
 * Validate contact form for Branch Locator
 */
export const validateBranchContactForm = ({ mobileNumber, email }) => {
  const errors = {};

  if (!mobileNumber && !email) {
    errors.mobileNumber = 'Please provide either mobile number or email';
    errors.email = 'Please provide either mobile number or email';
    return errors;
  }

  if (mobileNumber && !/^[6-9]\d{9}$/.test(mobileNumber)) {
    errors.mobileNumber = 'Enter valid mobile number';
  }

  if (email) {
    const trimmed = email.trim();

    if (trimmed.length > 254) {
      errors.email = 'Email address is too long';
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)
    ) {
      errors.email = 'Enter valid email';
    }
  }

  return errors;
};

export const validateBranchContactField = (name, value, form) => {
  // Cross-field rule
  if (name === 'mobileNumber' || name === 'email') {
    if (!form.mobileNumber && !form.email) {
      return 'Please provide either mobile number or email';
    }
  }

  if (name === 'mobileNumber' && value) {
    if (!/^[6-9]\d{9}$/.test(value)) {
      return 'Enter valid mobile number';
    }
  }

  if (name === 'email' && value) {
    const trimmed = value.trim();

    if (trimmed.length > 254) {
      return 'Email address is too long';
    }

    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)
    ) {
      return 'Enter valid email';
    }
  }

  return null;
};


/**
 * Safely strips HTML tags without using regex
 * O(n) time complexity – safe from ReDoS
 */
export const stripHTMLTags = (htmlString = '') => {
  if (typeof htmlString !== 'string') return '';

  const maxLength = 10000;
  const input =
    htmlString.length > maxLength
      ? htmlString.substring(0, maxLength)
      : htmlString;

  let result = '';
  let insideTag = false;

  for (const char of input) {
    if (char === '<') insideTag = true;
    else if (char === '>') insideTag = false;
    else if (!insideTag) result += char;
  }

  return result;
};